import React, { useState, useEffect } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { Container, Card, Row, Col, Button } from 'react-bootstrap';
import {
  FaMapMarkerAlt,
  FaArrowLeft,
  FaPhone,
  FaEnvelope,
  FaInfoCircle,
  FaStar,
  FaArrowRight,
  FaUserCircle,
  FaWhatsapp,
  FaGlobe,
  FaShare,
  FaHeart,
  FaImages,
  FaCheckCircle,
  FaClock,
  FaTag,
  FaThumbsUp
} from 'react-icons/fa';
import { spApi } from '../../api/serviceProviders/spApi';
import styles from '../styles/WatoaHudumaDetails.module.css';
import SpinnerLoader from '../../components/Buttons/SpinnerLoader';

const WatoaHudumaDetails = () => {
  const location = useLocation();
  const { id } = useParams();

  const { provider: stateProvider, category: navCategory, fallbackImage: navFallback } = location.state || {};

  const [provider, setProvider] = useState(stateProvider || null);
  const [loadingProvider, setLoadingProvider] = useState(!stateProvider);
  const [relatedProviders, setRelatedProviders] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');

  const fallbackImage = navFallback ||
    'https://via.placeholder.com/400x300?text=No+Image';

  useEffect(() => {
    const fetchProvider = async () => {
      setLoadingProvider(true);
      const data = await spApi.getProviderPublic(id);

      if (data) {
        // Extract provider object from response (e.g. data.data or data.provider or data)
        const providerData = (data?.data && typeof data.data === 'object' && (data.data.name || data.data.id))
          ? data.data
          : (data?.provider || data);

        if (providerData) {
          setProvider(providerData);
        }

        // Extract related service providers (related_sps)
        const related = data?.data?.related_sps || providerData?.related_sps || data?.related_sps || [];
        if (Array.isArray(related) && related.length > 0) {
          setRelatedProviders(related);
          setRelatedLoading(false);
        } else {
          // Fallback: fetch related providers by category if related_sps is empty
          let categoryId = null;
          if (providerData?.categories && providerData.categories.length > 0) {
            categoryId = providerData.categories[0]?.id;
          } else if (navCategory?.id) {
            categoryId = navCategory.id;
          }

          if (categoryId) {
            const result = await spApi.getCategoryDetails(categoryId, '');
            const filteredRelated = (result.providers || [])
              .filter(p => p && p.id !== providerData?.id);
            setRelatedProviders(filteredRelated);
          }
          setRelatedLoading(false);
        }
      } else {
        setRelatedLoading(false);
      }
      setLoadingProvider(false);
    };

    fetchProvider();
  }, [id, navCategory]);

  if (loadingProvider && !provider) {
    return (
      <Container className="my-5 text-center">
        <br /><br />
        <SpinnerLoader />
      </Container>
    );
  }

  if (!provider) {
    return (
      <Container className="my-5 text-center">
        <br /><br />
        <h4>Mtoa huduma hajapatikana</h4>
        <p className="text-muted">Service Provider not found</p>
        <Link to="/WatoaHuduma" className={styles.viewDetailsBtn}>
          Rudi kwenye Watoa Huduma
        </Link>
      </Container>
    );
  }

  // Get main wallpaper object (wallpaper can be an object or array)
  const wallpaperObj = Array.isArray(provider.wallpaper) ? provider.wallpaper[0] : provider.wallpaper;

  // Get main provider image
  const providerImage = wallpaperObj?.lg_photo ||
    wallpaperObj?.md_photo ||
    wallpaperObj?.sm_photo ||
    wallpaperObj?.photo_path ||
    fallbackImage;

  // Extract categories (from provider object, or navCategory, or fallback from related_sps)
  const categories = (provider.categories && provider.categories.length > 0)
    ? provider.categories
    : (navCategory ? [{ category: navCategory.name || navCategory.category }] : (relatedProviders.length > 0 && relatedProviders[0].categories ? relatedProviders[0].categories : []));

  // Extract location text safely
  const locationText = typeof provider.location === 'object' && provider.location !== null
    ? (provider.location.display_name || provider.location.name || provider.location.location)
    : provider.location;

  // Extract photos list for gallery
  const photos = Array.isArray(provider.photos) && provider.photos.length > 0
    ? provider.photos
    : (Array.isArray(provider.wallpaper) ? provider.wallpaper : (provider.wallpaper ? [provider.wallpaper] : []));

  // Format mobile & WhatsApp link
  const rawMobile = provider.mobile ? String(provider.mobile).trim() : '';
  const cleanMobile = rawMobile.replace(/\D/g, '');
  const waNumber = cleanMobile.startsWith('255') ? cleanMobile : (cleanMobile.startsWith('0') ? `255${cleanMobile.slice(1)}` : (cleanMobile ? `255${cleanMobile}` : ''));

  return (
    <div className={styles.providerPage}>
      <Container className="py-4">
        <br /><br />

        {/* Back Button */}
        <div className={styles.backNav}>
          <Link
            to={navCategory?.id ? `/watoa-huduma/category/${navCategory.id}` : '/WatoaHuduma'}
            className={styles.backLink}
          >
            <FaArrowLeft className={styles.backIcon} />
            Rudi Nyuma
          </Link>
        </div>

        {/* Provider Main Card */}
        <Card className={styles.providerCard}>
          <Row className="g-0">
            <Col md={4}>
              <div className={styles.providerImageWrapper}>
                <img
                  src={providerImage}
                  alt={provider.name}
                  className={styles.providerMainImage}
                />
                {provider.ratings > 0 && (
                  <div className={styles.ratingBadge}>
                    <FaStar className={styles.ratingStar} />
                    <span>{provider.ratings}</span>
                  </div>
                )}
              </div>
            </Col>
            <Col md={8}>
              <Card.Body className={styles.providerBody}>
                <div className={styles.providerHeader}>
                  <div className={styles.providerNameWrapper}>
                    <h1 className={styles.providerName}>{provider.name}</h1>
                    {categories.length > 0 && (
                      <span className={styles.categoryTag}>
                        {categories[0].category || categories[0].name}
                      </span>
                    )}
                  </div>
                  <div className={styles.providerActions}>
                    <button className={styles.actionIconBtn} title="Share">
                      <FaShare />
                    </button>
                    <button className={styles.actionIconBtn} title="Save">
                      <FaHeart />
                    </button>
                  </div>
                </div>

                <div className={styles.providerInfo}>
                  {locationText && (
                    <div className={styles.infoItem}>
                      <FaMapMarkerAlt className={styles.infoIcon} />
                      <span>{locationText}</span>
                    </div>
                  )}
                  {rawMobile && (
                    <div className={styles.infoItem}>
                      <FaPhone className={styles.infoIcon} />
                      <a href={`tel:${rawMobile}`} className={styles.infoLink}>
                        {rawMobile}
                      </a>
                    </div>
                  )}
                  {provider.email && (
                    <div className={styles.infoItem}>
                      <FaEnvelope className={styles.infoIcon} />
                      <a href={`mailto:${provider.email}`} className={styles.infoLink}>
                        {provider.email}
                      </a>
                    </div>
                  )}
                  {provider.website && (
                    <div className={styles.infoItem}>
                      <FaGlobe className={styles.infoIcon} />
                      <a href={provider.website} target="_blank" rel="noopener noreferrer" className={styles.infoLink}>
                        {provider.website}
                      </a>
                    </div>
                  )}
                </div>

                <div className={styles.providerContact}>
                  {rawMobile && (
                    <a href={`tel:${rawMobile}`} className={styles.contactBtn}>
                      <FaPhone className={styles.contactIcon} />
                      Piga Simu
                    </a>
                  )}
                  {waNumber && (
                    <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" className={styles.whatsappBtn}>
                      <FaWhatsapp className={styles.contactIcon} />
                      WhatsApp
                    </a>
                  )}
                </div>
              </Card.Body>
            </Col>
          </Row>
        </Card>

        {/* Tabs */}
        <div className={styles.tabsContainer}>
          <button
            className={`${styles.tabBtn} ${activeTab === 'details' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('details')}
          >
            <FaInfoCircle className={styles.tabIcon} />
            Maelezo
          </button>
          {photos.length > 0 && (
            <button
              className={`${styles.tabBtn} ${activeTab === 'photos' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('photos')}
            >
              <FaImages className={styles.tabIcon} />
              Picha ({photos.length})
            </button>
          )}
          <button
            className={`${styles.tabBtn} ${activeTab === 'related' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('related')}
          >
            <FaThumbsUp className={styles.tabIcon} />
            Wengine
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {activeTab === 'details' && (
            <div className={styles.detailsTab}>
              {provider.description && (
                <div className={styles.descriptionSection}>
                  <h3 className={styles.sectionTitle}>Maelezo</h3>
                  <p className={styles.descriptionText}>{provider.description}</p>
                </div>
              )}

              {categories.length > 0 && (
                <div className={styles.categoriesSection}>
                  <h3 className={styles.sectionTitle}>Kategoria</h3>
                  <div className={styles.categoriesList}>
                    {categories.map((cat, index) => (
                      <span key={index} className={styles.categoryChip}>
                        {cat.category || cat.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'photos' && photos.length > 0 && (
            <div className={styles.photosTab}>
              <Row className="g-3">
                {photos.map((photo, pIdx) => (
                  <Col key={photo.id || pIdx} md={4} lg={3}>
                    <div className={styles.photoItem}>
                      <img
                        src={photo.lg_photo || photo.md_photo || photo.sm_photo || photo.photo_path}
                        alt={photo.caption || 'Provider photo'}
                        className={styles.photoImage}
                      />
                      {photo.caption && (
                        <div className={styles.photoCaption}>{photo.caption}</div>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {activeTab === 'related' && (
            <div className={styles.relatedTab}>
              {relatedLoading ? (
                <div className="text-center py-4">
                  <SpinnerLoader />
                </div>
              ) : relatedProviders.length > 0 ? (
                <Row className="g-4">
                  {relatedProviders.map((related) => {
                    const wp = Array.isArray(related.wallpaper) ? related.wallpaper[0] : related.wallpaper;
                    const relatedImage = wp?.lg_photo ||
                      wp?.md_photo ||
                      wp?.sm_photo ||
                      wp?.photo_path ||
                      fallbackImage;

                    const relatedCategories = related.categories || [];
                    const relatedLoc = typeof related.location === 'object' && related.location !== null
                      ? (related.location.display_name || related.location.name)
                      : related.location;

                    return (
                      <Col key={related.id} md={6} lg={4}>
                        <Card className={styles.relatedCard}>
                          <div className={styles.relatedImageWrapper}>
                            <img
                              src={relatedImage}
                              alt={related.name}
                              className={styles.relatedImage}
                            />
                            {relatedCategories.length > 0 && (
                              <span className={styles.relatedCategoryBadge}>
                                {relatedCategories[0].category || relatedCategories[0].name}
                              </span>
                            )}
                          </div>
                          <Card.Body className={styles.relatedBody}>
                            <h5 className={styles.relatedName}>{related.name}</h5>
                            {relatedLoc && (
                              <div className={styles.relatedLocation}>
                                <FaMapMarkerAlt className={styles.relatedLocationIcon} />
                                <span>{relatedLoc}</span>
                              </div>
                            )}
                            <Link
                              to={`/watoa-huduma-details/${related.id}`}
                              state={{ provider: related }}
                              className={styles.relatedViewBtn}
                            >
                              Angalia Huduma
                              <FaArrowRight className={styles.relatedViewIcon} />
                            </Link>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              ) : (
                <div className={styles.noRelated}>
                  <p className="text-muted">Hakuna watoa huduma wengine katika kategoria hii</p>
                </div>
              )}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default WatoaHudumaDetails;